import { useState, useMemo, useCallback, ChangeEvent } from 'react'
import { FormDialog } from 'components/dialog/form'

import {
  Stack,
  Button,
  Checkbox,
  List, 
  ListItem,
  ListItemButton,
  ListItemText,
  Paper
} from '@mui/material'

import {
  DragIndicator
} from '@mui/icons-material'

import { ColumnDefs } from '../constants'

import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { arrayMove, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

type SortableItem = {
  id: string
  label: string
  checked: boolean
}

type SortableItemRowProps = {
  item: SortableItem
  onChange: (id: string, checked: boolean) => void
}

const SortableItemRow = ({
  item,
  onChange
}: SortableItemRowProps) => {
  const {
    setNodeRef,
    setActivatorNodeRef,
    transform, transition,
    attributes, listeners,
    isDragging,
  } = useSortable({ id: item.id })

  const onChangeHandleCheck = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      onChange(item.id, e.target.checked)
    },
    [onChange, item]
  )
  return (
    <Paper
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition
      }}
      sx={{
        position: 'relative',
        zIndex: isDragging ? 10 : 1
      }}
      elevation={isDragging ? 3 : 0}
      square
    >
      <ListItem
        sx={{ alignItems: 'stretch' }}
        disablePadding
      >
        <Stack
          ref={setActivatorNodeRef}
          direction="column"
          justifyContent="center"
          paddingX={0.5}
          sx={{
            cursor: isDragging ? "grabbing" : "grab"
          }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator/>
        </Stack>
        
        <ListItemButton dense onClick={() => onChange(item.id, !item.checked)}>
          <Checkbox
            edge="start"
            checked={item.checked}
            tabIndex={-1}
            disableRipple
            inputProps={{'aria-labelledby': item.id }}
            onChange={onChangeHandleCheck}
            sx={{
              padding: 0.5
            }}
          />
          <ListItemText id={item.id} primary={item.label}/>
        </ListItemButton>
      </ListItem>
    </Paper>
  )
}

type EditKojinListColumnsDialogProps = {
  checkedColumns: string[]
  onSubmit: (newColumns: string[]) => void
  onClose: () => void
}
export const EditKojinListColumnsDialog = ({
  checkedColumns,
  onSubmit,
  onClose
}: EditKojinListColumnsDialogProps) => {
  const sensors = useSensors(useSensor(PointerSensor))

  const [columns, setColumns] = useState<SortableItem[]>(
    checkedColumns.flatMap(
      (col) => {
        const def = ColumnDefs[col];
        if (!def) return []
        return { id: col, label: def.label, checked: true }
      }
    )
    .concat(
      Object.keys(ColumnDefs).flatMap(
        (defKey) => {
          if (checkedColumns.some((col) => defKey === col)) return []
          return { id: defKey, label: ColumnDefs[defKey].label, checked: false }
        }
      )
    )
  )
  const checkedCount = useMemo(() => columns.filter((col) => col.checked).length, [columns]);

  const onChangeColumnChecked = useCallback(
    (id: string, checked: boolean) => {
      setColumns(columns.map((item) => (id === item.id || id === 'all') ? ({ ...item, checked }) : item ))
    },
    [setColumns, columns]
  )
  const handleOnAllChecked = useCallback(
    (checked: boolean) => onChangeColumnChecked('all', checked),
    [onChangeColumnChecked]
  )
  const onMoveColumnList = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (over) {
        setColumns(arrayMove(
          columns,
          columns.findIndex((column) => column.id === active.id),
          columns.findIndex((column) => column.id === over.id)
        ))
      }
    },
    [columns, setColumns]
  )

  return (
    <FormDialog
      open
      fullWidth
      maxWidth="xs"
      disablePadding
      title={`${checkedCount} / ${columns.length} 項目 選択`}
      body={(
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          modifiers={[restrictToParentElement, restrictToVerticalAxis]}
          onDragEnd={onMoveColumnList}
        >
          <SortableContext items={columns} strategy={verticalListSortingStrategy}>
            <List disablePadding>
              { columns.map((col) => <SortableItemRow key={col.id} item={col} onChange={onChangeColumnChecked}/>) }
            </List>
          </SortableContext>
        </DndContext>
      )}
      leftAction={
        <Button
          variant="contained"
          size="large"
          onClick={() => handleOnAllChecked(checkedCount < columns.length)}
        >
          {checkedCount < columns.length ? "全選択" : "全解除"}
        </Button>
      }
      isValid={checkedCount > 0}
      submitLabel="保存"
      onSubmit={(event) => {
        event.preventDefault()
        const checkedList = columns.filter((col) => col.checked)
        if (checkedList.length === 0) return
        onSubmit(checkedList.map((col) => col.id))
      }}      
      onClose={onClose}
    />
  )
}
